import { useState, useEffect } from 'react'
import { supabase } from '../supabase'
import { computeDerivedAlerts } from '../services/alertEngine'

export function usePatients(profesionalId) {
  const [patients, setPatients] = useState([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    if (!profesionalId) return
    fetchPatients()
  }, [profesionalId]) // eslint-disable-line react-hooks/exhaustive-deps

  async function fetchPatients() {
    setLoading(true)
    const { data, error } = await supabase
      .from('professional_patient')
      .select(`
        paciente_id,
        patients (
          id, nombre, fecha_nacimiento, sexo, altura_cm,
          patient_goals (
            tipo, kcal_objetivo, proteina_objetivo,
            grasa_objetivo, carbohidratos_objetivo,
            peso_objetivo, grasa_objetivo_pct
          ),
          body_metrics (
            fecha, peso_kg, grasa_pct,
            masa_magra_pct, perimetro_abdominal_cm, metodo
          ),
          activity_profile (
    nivel, entrenamientos_semana, bmr, tdee, kcal_objetivo_calculado
  ),
          alerts (
            id, tipo, nivel, mensaje, fecha, resuelta
          ),
          daily_logs ( fecha )
        )
      `)
      .eq('profesional_id', profesionalId)

    if (!error && data) {
      const parsed = data.map(d => {
        const p = d.patients
        const metrics = p.body_metrics?.sort((a, b) => new Date(b.fecha) - new Date(a.fecha))
        const latest = metrics?.[0]
        const goals = p.patient_goals?.[0]
        const dbAlerts = p.alerts?.filter(a => !a.resuelta) ?? []
        const derivedAlerts = computeDerivedAlerts({
          goals,
          allMetrics: metrics ?? [],
          dailyLogFechas: p.daily_logs?.map(l => l.fecha) ?? [],
        })
        const activeAlerts = [...dbAlerts, ...derivedAlerts]
        const status = activeAlerts.some(a => a.nivel === 'critica') ? 'red'
          : activeAlerts.some(a => a.nivel === 'aviso') ? 'yellow' : 'green'
        return {
          id: p.id,
          nombre: p.nombre,
          sexo: p.sexo,
          altura_cm: p.altura_cm,
          fecha_nacimiento: p.fecha_nacimiento,
          peso: latest?.peso_kg ?? null,
          grasa_pct: latest?.grasa_pct ?? null,
          masa_magra_pct: latest?.masa_magra_pct ?? null,
          perimetro: latest?.perimetro_abdominal_cm ?? null,
          goals: goals ?? null,
          activityProfile: p.activity_profile?.[0] ?? null,
          alerts: activeAlerts,
          allMetrics: metrics ?? [],
          status,
        }
      })
      setPatients(parsed)
    }
    setLoading(false)
  }

  async function eliminarPaciente(pacienteId) {
    // Eliminar asignación profesional-paciente
    await supabase
      .from('professional_patient')
      .delete()
      .eq('paciente_id', pacienteId)
      .eq('profesional_id', profesionalId)
  
    // Eliminar datos del paciente
    await supabase.from('body_metrics').delete().eq('paciente_id', pacienteId)
    await supabase.from('patient_goals').delete().eq('paciente_id', pacienteId)
    await supabase.from('clinical_notes').delete().eq('paciente_id', pacienteId)
    await supabase.from('alerts').delete().eq('paciente_id', pacienteId)
    await supabase.from('activity_logs').delete().eq('paciente_id', pacienteId)
  
    // Eliminar logs de comidas
    const { data: logs } = await supabase
      .from('daily_logs')
      .select('id')
      .eq('paciente_id', pacienteId)
  
    if (logs?.length) {
      const logIds = logs.map(l => l.id)
      const { data: meals } = await supabase
        .from('meals')
        .select('id')
        .in('daily_log_id', logIds)
      if (meals?.length) {
        await supabase.from('meal_items').delete().in('meal_id', meals.map(m => m.id))
        await supabase.from('meals').delete().in('daily_log_id', logIds)
      }
      await supabase.from('daily_logs').delete().eq('paciente_id', pacienteId)
    }
  
    await supabase.from('patients').delete().eq('id', pacienteId)
    await fetchPatients()
  }
  
  async function fetchPatientHistory(patientId, days = 14) {
    const desde = new Date()
    desde.setDate(desde.getDate() - days)
    const desdeStr = desde.toISOString().split('T')[0]
  
    const { data } = await supabase
      .from('daily_logs')
      .select(`
        fecha,
        meals (
          meal_items (
            cantidad_gramos,
            foods ( kcal_100g, proteina_100g, grasa_100g, carbohidratos_100g )
          )
        )
      `)
      .eq('paciente_id', patientId)
      .gte('fecha', desdeStr)
      .order('fecha', { ascending: true })
  
    if (!data) return []
  
    return data.map(log => {
      const items = log.meals?.flatMap(m => m.meal_items ?? []) ?? []
      const kcal = items.reduce((s, i) => s + (i.foods?.kcal_100g * i.cantidad_gramos / 100 || 0), 0)
      const prot = items.reduce((s, i) => s + (i.foods?.proteina_100g * i.cantidad_gramos / 100 || 0), 0)
      const gras = items.reduce((s, i) => s + (i.foods?.grasa_100g * i.cantidad_gramos / 100 || 0), 0)
      const carb = items.reduce((s, i) => s + (i.foods?.carbohidratos_100g * i.cantidad_gramos / 100 || 0), 0)
      return {
        fecha: log.fecha,
        label: new Date(log.fecha).toLocaleDateString('es', { day: '2-digit', month: 'short' }),
        kcal: Math.round(kcal),
        prot: Math.round(prot),
        gras: Math.round(gras),
        carb: Math.round(carb),
      }
    })
  }
  
  return { patients, loading, refetch: fetchPatients, fetchPatientHistory, eliminarPaciente }

}